import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider } from './AuthContext';
import { SearchProvider } from './SearchContext';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CartProvider } from './CartContext'; // Correct path
import { UndoProvider } from './UndoContext';
import UndoSnackbar from './components/UndoSnackbar';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTopButton from './components/ScrollToTopButton';

// Lazy load all page components for performance
const Home = React.lazy(() => import('./pages/Home'));
const Shop = React.lazy(() => import('./pages/Shop'));
const Blog = React.lazy(() => import('./pages/Blog'));
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Cart = React.lazy(() => import('./pages/Cart'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const CheckoutPage = React.lazy(() => import('./pages/CheckoutPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));

function App() {
  const location = useLocation();

  return (
    <>
      <div className="App">
        <Header />
        <AnimatePresence mode="wait">
          <Suspense fallback={<div className="page-loader"></div>}>
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/profile" element={<ProfilePage />}/>
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
            </Routes>
          </Suspense>
        </AnimatePresence>
        <Footer />
        <ScrollToTopButton />
      </div>
      <Toaster
          position="bottom-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: 'var(--color-bg-shaded)',
              color: 'var(--color-text)',
              border: '1px solid var(--color-border)',
              boxShadow: 'var(--shadow-lg)',
            },
            success: {
              iconTheme: {
                primary: '#2563eb',
                secondary: 'white',
              },
            },
          }}
        />
        <UndoSnackbar />
    </>
  );
}

function AppWrapper() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <SearchProvider>
              <UndoProvider>
                <CartProvider>
                  <App />
                </CartProvider>
              </UndoProvider>
            </SearchProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default AppWrapper;
